import { Logo } from '@/components/logo';
import transitions from '@/theme/core/transitions';
import type { BackdropProps as MuiBackdropProps } from '@mui/material';
import MuiBackdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import { alpha, styled } from '@mui/material/styles';
import { forwardRef } from 'react';

// 扩展 MUI 的 BackdropProps
export interface BackdropProps extends Omit<MuiBackdropProps, 'invisible'> {
  color?: string;
  duration?: number; // 添加自定义过渡时间参数
  showLogo?: boolean; // 添加是否显示logo的选项
}

// 创建样式化组件
const BackdropRoot = styled(MuiBackdrop, {
  shouldForwardProp: (prop) => !['color', 'duration', 'showLogo'].includes(prop as string),
})<BackdropProps>(({ theme, color, duration }) => ({
  position: 'fixed',
  backdropFilter: 'blur(24px) saturate(140%) brightness(0.95) contrast(1.08)',
  backgroundColor: color || alpha(theme.palette.background.default, 0.1),
  transitionDuration: `${duration || transitions.durations.overlay}ms !important`,
  transitionTimingFunction: transitions.easings.emphasized,
  '&.MuiBackdrop-invisible': {
    backgroundColor: 'transparent',
  },
}));

// 创建 Backdrop 组件
export const Backdrop = forwardRef<HTMLDivElement, BackdropProps>((props, ref) => {
  const { color, duration, children, showLogo = true, ...other } = props;

  return (
    <BackdropRoot ref={ref} color={color} duration={duration} {...other}>
      {showLogo && (
        <Box
          sx={{
            position: 'absolute',
            top: '16px',
            left: '80px',
            zIndex: 10,
          }}
        >
          <Logo />
        </Box>
      )}
      {children}
    </BackdropRoot>
  );
});

Backdrop.displayName = 'Backdrop';

// 导出默认配置
export const defaultBackdropConfig = {
  blur: 24,
  brightness: 0.95,
  saturation: 1.4,
  contrast: 1.08,
  opacity: 0.1,
};
